import React, { Fragment } from 'react';
import { useTheme } from '../contexts/ThemeContext';
// Types for resource items
type ResourceType = 'handwritten' | 'typed' | 'stenciled';
interface ResourceItem {
  id: string;
  type: ResourceType;
  title: string;
  content: string;
  rotation: number;
  attachment: 'pin' | 'tape' | 'clip';
  hasStain?: boolean;
  hasFold?: boolean;
  hasHighlight?: string[];
  highlightColor?: string;
  category: 'offer' | 'need' | 'event' | 'resource';
  urgent?: boolean;
}
export function CommunityResourceWall() {
  const {
    currentEra
  } = useTheme();
  // Sample resource data
  const resources: ResourceItem[] = [{
    id: '1',
    type: 'handwritten',
    title: 'FREE LEGAL AID',
    content: 'Every Tuesday 6-8PM\nBring ID and any relevant documents\nNo appointment needed',
    rotation: -2,
    attachment: 'pin',
    hasHighlight: ['Every Tuesday'],
    highlightColor: 'yellow',
    category: 'offer'
  }, {
    id: '2',
    type: 'typed',
    title: 'HOUSING RESOURCES',
    content: 'Updated list of LGBTQ+ friendly housing options and roommate connections. Take a photo or contact number below.',
    rotation: 1,
    attachment: 'tape',
    hasStain: true,
    category: 'resource'
  }, {
    id: '3',
    type: 'stenciled',
    title: 'RALLY FOR RIGHTS',
    content: 'JUNE 28\nCITY HALL\n5PM\nBRING SIGNS & FRIENDS',
    rotation: -1,
    attachment: 'pin',
    hasFold: true,
    category: 'event',
    urgent: true
  }, {
    id: '4',
    type: 'handwritten',
    title: 'NEED VOLUNTEERS',
    content: 'Help staff the center on weekends\nEven just 2 hours helps!\nSign up sheet at front desk',
    rotation: 2,
    attachment: 'tape',
    hasHighlight: ['weekends', '2 hours'],
    highlightColor: 'pink',
    category: 'need'
  }, {
    id: '5',
    type: 'typed',
    title: 'SUPPORT GROUP',
    content: 'Thursdays 7PM\nFacilitated by Dr. Martinez\nAll welcome\nSnacks provided',
    rotation: -1.5,
    attachment: 'clip',
    hasFold: true,
    hasHighlight: ['Thursdays 7PM'],
    highlightColor: 'cyan',
    category: 'offer'
  }, {
    id: '6',
    type: 'stenciled',
    title: 'DONATIONS NEEDED',
    content: 'WINTER CLOTHING\nNON-PERISHABLE FOOD\nHYGIENE PRODUCTS\nDROP OFF ANYTIME',
    rotation: 1.5,
    attachment: 'pin',
    category: 'need',
    urgent: true
  }, {
    id: '7',
    type: 'handwritten',
    title: 'FREE ZINE WORKSHOP',
    content: 'Learn to make your own zines!\nAll materials provided\nSaturday 2-4PM\nNo experience needed',
    rotation: -3,
    attachment: 'tape',
    hasStain: true,
    hasHighlight: ['Saturday 2-4PM'],
    highlightColor: 'yellow',
    category: 'offer'
  }, {
    id: '8',
    type: 'typed',
    title: 'KNOW YOUR RIGHTS',
    content: 'Free pocket guides available at front desk. Information on dealing with police, landlords, and employers.',
    rotation: 2,
    attachment: 'clip',
    hasFold: true,
    category: 'resource'
  }, {
    id: '9',
    type: 'stenciled',
    title: 'COMMUNITY MEETING',
    content: 'MONTHLY PLANNING\nJULY 10\n7PM\nALL VOICES NEEDED',
    rotation: -2,
    attachment: 'pin',
    category: 'event'
  }];
  // Group resources by category
  const offers = resources.filter(r => r.category === 'offer');
  const needs = resources.filter(r => r.category === 'need');
  const events = resources.filter(r => r.category === 'event');
  const resourceItems = resources.filter(r => r.category === 'resource');
  // Get appropriate wall background based on era
  const getWallBackground = () => {
    switch (currentEra) {
      case '60s':
        return 'bg-gray-200';
      case '70s':
        return 'bg-amber-50';
      case '80s':
      case '90s':
        return 'bg-gray-900';
      case '2000s':
        return 'bg-white';
      default:
        return 'bg-gray-200';
    }
  };
  // Render attachment elements (pins, tape, clips)
  const renderAttachment = (type: string, rotation: number) => {
    switch (type) {
      case 'pin':
        return <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20" data-id="element-27">
            <div className="w-3 h-3 rounded-full bg-red-500 shadow-sm" data-id="element-28"></div>
          </div>;
      case 'tape':
        return <>
            <div className="absolute -top-3 left-1/4 w-10 h-5 bg-gray-100 bg-opacity-60 transform -rotate-12 z-20" data-id="element-29"></div>
            <div className="absolute -top-3 right-1/4 w-10 h-5 bg-gray-100 bg-opacity-60 transform rotate-12 z-20" data-id="element-30"></div>
          </>;
      case 'clip':
        return <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20" data-id="element-31">
            <div className="w-6 h-8 border-2 border-gray-400 rounded-t-lg bg-gray-300" data-id="element-32"></div>
          </div>;
      default:
        return null;
    }
  };
  // Function to highlight specific text
  const highlightText = (text: string, highlights: string[], color: string) => {
    let result = text;
    highlights.forEach(highlight => {
      const parts = result.split(highlight);
      if (parts.length > 1) {
        const colorClass = color === 'yellow' ? 'bg-yellow-300' : color === 'pink' ? 'bg-pink-300' : color === 'cyan' ? 'bg-cyan-300' : 'bg-green-300';
        result = parts.join(`<span class="${colorClass} px-0.5">${highlight}</span>`);
      }
    });
    return <div dangerouslySetInnerHTML={{
      __html: result.replace(/\n/g, '<br/>')
    }} data-id="element-33" />;
  };
  // Render a resource item
  const renderResourceItem = (resource: ResourceItem) => {
    const baseClasses = `relative p-4 shadow-md transform overflow-hidden z-10 max-w-xs`;
    const rotationClass = `rotate-[${resource.rotation}deg]`;
    // Type-specific styling
    let typeClasses = '';
    switch (resource.type) {
      case 'handwritten':
        typeClasses = 'bg-white font-handwritten text-black';
        break;
      case 'typed':
        typeClasses = 'bg-gray-100 font-mono text-black';
        break;
      case 'stenciled':
        typeClasses = 'bg-black text-white font-stencil uppercase tracking-wider';
        break;
    }
    // Urgency styling
    const urgencyClass = resource.urgent ? 'border-l-4 border-red-500' : '';
    // Visual markers
    const stainClass = resource.hasStain ? "before:content-[''] before:absolute before:top-1/4 before:left-1/4 before:w-16 before:h-16 before:bg-amber-800 before:bg-opacity-10 before:rounded-full before:-z-10" : '';
    const foldClass = resource.hasFold ? "after:content-[''] after:absolute after:top-0 after:right-0 after:w-full after:h-[1px] after:bg-gray-400 after:opacity-30" : '';
    return <div className={`${baseClasses} ${typeClasses} ${rotationClass} ${urgencyClass} ${stainClass} ${foldClass}`} data-id="element-34">
        {renderAttachment(resource.attachment, resource.rotation)}
        <h3 className="font-bold text-lg mb-2" data-id="element-35">
          {resource.type === 'stenciled' ? resource.title : resource.title}
        </h3>
        <div className="text-sm" data-id="element-36">
          {resource.hasHighlight ? highlightText(resource.content, resource.hasHighlight, resource.highlightColor || 'yellow') : resource.content.split('\n').map((line, i) => <Fragment key={i} data-id="element-37">
                  {line}
                  <br data-id="element-38" />
                </Fragment>)}
        </div>
      </div>;
  };
  // Render a category section
  const renderCategorySection = (title: string, items: ResourceItem[]) => {
    return <div className="mb-8" data-id="element-39">
        <div className="inline-block mb-4 px-4 py-2 bg-black text-white font-bold tracking-widest relative transform -rotate-1" data-id="element-40">
          <div className="absolute -top-2 -left-2 w-3 h-3 rounded-full bg-red-500" data-id="element-41"></div>
          <div className="absolute -top-2 -right-2 w-3 h-3 rounded-full bg-red-500" data-id="element-42"></div>
          {title}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-43">
          {items.map(item => <div key={item.id} className="transform hover:-translate-y-1 transition-transform duration-200" data-id="element-44">
              {renderResourceItem(item)}
            </div>)}
        </div>
      </div>;
  };
  return <div className={`p-6 ${getWallBackground()} min-h-screen`} data-id="element-45">
      <div className="max-w-6xl mx-auto" data-id="element-46">
        <div className="mb-8 text-center" data-id="element-47">
          <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-wider mb-2 inline-block transform -rotate-1 bg-black text-white px-4 py-2" data-id="element-48">
            Community Resource Wall
          </h2>
          <div className="text-sm italic mt-4 opacity-70" data-id="element-49">
            {currentEra === '90s' || currentEra === '80s' ? 'Take what you need. Leave what you can. We keep us safe.' : 'Please add your resources and needs to the appropriate sections. Updated weekly.'}
          </div>
        </div>
        {renderCategorySection('WE OFFER', offers)}
        {renderCategorySection('COMMUNITY NEEDS', needs)}
        {renderCategorySection('UPCOMING EVENTS', events)}
        {renderCategorySection('RESOURCES', resourceItems)}
      </div>
    </div>;
}